import { configureStore } from '@reduxjs/toolkit';
import { isImmutable } from 'immutable';
import { routerMiddleware } from 'react-router-redux';
import createSagaMiddleware from 'redux-saga';
import { createReducerManager } from '@dbh/create-reducer-redux';
import { REDUX_AND_SAGA_KEY, loggerReducer } from '@dbh/logger-redux';
import customMiddlewares, { validateMiddlewareOrder } from '@dbh/redux-middlewares';
import { reducerInjected } from '@dbh/redux-extra';

/**
 *
 * `isSerializable`: `@dbh/configure-redux-store`.
 *
 *//**
 * Check if a value is serializable for `Redux` state management purposes.
 * While `Redux` typically requires all state values to be plain serializable
 * objects, we specifically override this check to allow `Immutable.js` objects
 * in our state. This is safe because `Immutable.js` objects have their own
 * serialization methods and maintain data integrity.
 * Currently, we only reject function values as they cannot be serialized and
 * should not be stored in `Redux` state.
 * @param {any} value The value to check for serializability.
 * @return {boolean} `true` if the value can be safely stored in `Redux` state.
 */const isSerializable=a=>"function"!=typeof a;var isSerializable$1 = isSerializable;

/**
 * Configures the `Redux` store with asynchronously loaded reducers and `sagas`.
 * @param {Object} initialState The initial state for the store.
 * @param {Object} history The history for the router.
 * @param {Object} options Additional options for the store configuration.
 * @return {Object} The configured `Redux` store.
 */const configureReduxStore=function(a,b,c){void 0===a&&(a={}),void 0===c&&(c={});const{sagaErrorHandler:d,useLoggerInWWW:e,sentryReduxEnhancer:f}=c,g=d?{onError:d}:{},h=createSagaMiddleware(g),i=[...customMiddlewares,routerMiddleware(b),h];// `Sagas` will swallow errors so we need an option to handle them.
// Create the store with two middlewares:
// 0. `customMiddlewares`: they have to be in the first position because they
// dispatch events that `redux-saga` `SAGAs` must receive and process normally.
// 1. `routerMiddleware`: syncs the location (`URL`) path to the state.
// 2. `sagaMiddleware`: makes `redux-sagas` work.
validateMiddlewareOrder(i,{sagaMiddleware:h});const j={};// We don't want to activate the `loggerReducer` in `PRODUCTION_WWW`, unless
// its activation cookie is set from the `devbar`.
e&&(j[REDUX_AND_SAGA_KEY]=loggerReducer);// Create the reducer first to ensure proper initialization.
const k=createReducerManager(j,a),l=Object.keys(a).reduce((b,c)=>(k.getReducerMap()[c]&&(b[c]=a[c]),b),{}),m=configureStore({reducer:k.reduce,preloadedState:l,middleware:a=>// In Production, `Redux`'s default validation middlewares (`serializableCheck`,
// `immutableCheck`, `actionCreatorCheck`) will be automatically excluded by
// `Redux` Toolkit for better performance.
// @see {@link https://redux-toolkit.js.org/api/getDefaultMiddleware#production}
a({// Currently we have non-serializable state in `dateFnsLocale` and
// `CancellableReduxSagaSpawn` reducers.
// @TODO: Find a better way to store the `dateFns` and `CancellableReduxSagaSpawn`
// functions instead of having them in the state.
serializableCheck:{isSerializable: isSerializable$1,ignoredPaths:["dateFnsLocales","CancellableReduxSagaSpawn","ContainerWithEntryContent"],ignoredActionPaths:["payload.dateFnsLocale","payload.spawnedTask",// Contains functions @example `onMenuItemClick`.
// @see `@dbh/top-menu`.
"payload.entryContentProps"]},immutableCheck:{// This is the default implementation of `immutableCheck` in addition
// to the `isImmutable` from `Immutable.js`.
// @see {@link https://redux-toolkit.js.org/api/immutabilityMiddleware#isimmutabledefault}
isImmutable:a=>"object"!=typeof a||null===a||"undefined"==typeof a||// @TODO: Remove this once we completely remove `Immutable.js`
// from the codebase.
isImmutable(a)},// eslint-disable-next-line id-blacklist
thunk:!1,actionCreatorCheck:!0}).prepend(...i),enhancers:a=>a().concat(...(f?[f]:[])),devTools:!1}),n=m.dispatch;/**
   * Filter the initial state to only include keys that have corresponding
   * reducers. This prevents `Redux` from logging warnings about unexpected keys
   * in the state. While `Redux` automatically ignores unknown state keys, it
   * still logs warning messages about unexpected keys. This filtering step
   * eliminates these warnings by only passing through state values that have
   * matching reducers registered.
   * @example "Unexpected keys [...] found in preloaded state.
   * Expected to find one of the known reducer keys instead: [...]"
   */// Extensions.
return m.runSaga=a=>h.run(a,{limitedUseDispatch:n}),m.injectedSagas={},m.createReducer=(b,c)=>{Reflect.has(k.getReducerMap(),b)||(k.add(b,c),m.dispatch(reducerInjected(b,a[b])));},m.reducerManager=k,m};

export { configureReduxStore as default };
